/* Admin "Branding" tab — month-by-month QR-branding revenue chart.
   Data: GET /api/admin/branding/revenue (last 12 months, zero-filled). */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import logging from "../utils/logging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtNaira = (n) => `\u20a6${Number(n || 0).toLocaleString()}`;
const fmtMonth = (m) => {
  const [y, mo] = m.split("-");
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString(undefined, { month: "short", year: "2-digit" });
};

function Tile({ label, value, sub, accent = "text-gray-900", testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</p>
      <p className={`font-heading text-2xl font-black tracking-tighter mt-1 ${accent}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500 font-body mt-0.5">{sub}</p>}
    </div>
  );
}

export const BrandingRevenueTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/branding/revenue`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      logging.error("branding revenue", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <p className="text-xs text-gray-400 font-body py-12 text-center">Loading…</p>;
  if (!data) return <p className="text-xs text-gray-400 font-body py-12 text-center">Failed to load branding data</p>;

  const chartData = data.months.map((m) => ({ ...m, label: fmtMonth(m.month) }));
  const hasAny = data.total_purchases > 0;

  return (
    <div className="space-y-4" data-testid="branding-revenue-tab">
      <div className="grid grid-cols-3 gap-3">
        <Tile
          label={t("admin_activity.branding_total_revenue")}
          value={fmtNaira(data.total_revenue_ngn)}
          accent="text-emerald-700"
          testId="branding-total-revenue"
        />
        <Tile
          label={t("admin_activity.branding_buyers")}
          value={data.total_purchases}
          testId="branding-total-buyers"
        />
        <Tile
          label={t("admin_activity.branding_this_month")}
          value={fmtNaira(data.this_month_revenue_ngn)}
          sub={`${data.this_month_purchases} × ${fmtNaira(data.price_ngn)}`}
          testId="branding-this-month"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="branding-revenue-chart">
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gray-700 mb-3">
          {t("admin_activity.branding_chart_title")}
        </p>
        {!hasAny ? (
          <p className="text-xs text-gray-400 font-body py-10 text-center">
            {t("admin_activity.branding_no_data")}
          </p>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
                <YAxis
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `\u20a6${v / 1000}k` : `\u20a6${v}`)}
                  width={52}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  formatter={(value, name) => (name === "revenue_ngn" ? [fmtNaira(value), "Revenue"] : [value, "Purchases"])}
                  labelStyle={{ fontSize: 11, fontWeight: 700 }}
                  contentStyle={{ fontSize: 11, borderRadius: 2, border: "1px solid #e5e7eb" }}
                />
                <Bar dataKey="revenue_ngn" fill="#059669" radius={[2, 2, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-[10px] text-gray-400 font-body italic mt-2">
          {t("admin_activity.branding_price_note", { price: Number(data.price_ngn).toLocaleString() })}
        </p>
      </div>
    </div>
  );
};

export default BrandingRevenueTab;
